/**
 * MA-PHASE11A: Account Switcher Component
 * 
 * Allows users to switch between accounts and manage sub-accounts.
 * 
 * CORRECTIONS APPLIED:
 * - Visibility: Show if (>1 account OR can_create_subaccounts OR can_manage OR super_admin)
 * - Smart route handling: Stay on current route if allowed, else redirect
 * - Real-time search: case-insensitive, matches account.name
 * - Post-creation: Auto-switch to new sub-account (Option A)
 * - NO impersonation features
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { isPathReachable, getFirstReachablePath } from "@/lib/navigationConfig";
import { usePermissions } from "@/hooks/usePermissions";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Building2, Check, ChevronDown, Plus, Search, Settings as SettingsIcon } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreateSubAccountModal } from "@/components/CreateSubAccountModal";

/**
 * Account Switcher Component
 * 
 * MA-PHASE11A: Enhanced with search, sub-account management, and smart switching.
 * Adapts to sidebar collapsed state (icon only vs full display).
 */
export function AccountSwitcher({ collapsed = false }) {
  // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001: Use accounts from TenantContext
  const { 
    selectedTenantId, 
    setSelectedTenantId, 
    isSuperAdmin, 
    accounts, 
    currentAccount,
    refreshAccounts,
    loading: accountsLoading 
  } = useTenant();
  const { effectiveModules } = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001: No longer fetching accounts independently
  // Accounts are now provided by TenantContext
  
  // MA-PHASE11A-BUGFIX-001: Debug log current selection
  useEffect(() => {
    if (currentAccount) {
      // MA-PHASE11A: Debug logging commented out for production
      // console.log('[Switcher] Current Account:', {
      //   name: currentAccount.account_name,
      //   id: currentAccount.account_id,
      //   type: currentAccount.account_type,
      //   role: currentAccount.role,
      //   matchesTenantId: currentAccount.account_id === selectedTenantId
      // });
    } else if (selectedTenantId) {
      // console.warn('[Switcher] selectedTenantId set but no matching account found:', selectedTenantId);
    }
  }, [currentAccount, selectedTenantId]);
  
  // Determine if switcher should be visible
  // CORRECTION: Show if (>1 account OR can_create_subaccounts OR can_manage OR super_admin)
  const shouldShow = 
    isSuperAdmin || 
    accounts.length > 1 || 
    currentAccount?.can_manage || 
    currentAccount?.can_create_subaccounts;

  // Filter accounts by search query (case-insensitive, account.name only)
  const filteredAccounts = accounts.filter(account => 
    account.account_name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // MA-PHASE11A DEBUG: Log filtered results
  React.useEffect(() => {
    if (searchQuery) {
      // console.log('[AccountSwitcher] filtered:', filteredAccounts.length);
    }
  }, [searchQuery, filteredAccounts.length]);

  // Handle account switch
  const handleSwitchAccount = async (accountId) => {
    if (accountId === selectedTenantId) return; // Already selected

    try {
      // MA-PHASE11A: Debug logging commented out
      // console.log('[AccountSwitch] START:', {
      //   from: selectedTenantId,
      //   to: accountId,
      //   fromName: currentAccount?.account_name,
      //   toName: accounts.find(a => a.account_id === accountId)?.account_name
      // });
      
      // MA-PHASE11A-BUGFIX-001: Proper flow order
      // 1. Update selectedTenantId (triggers TenantContext update)
      setSelectedTenantId(accountId);
      
      // 2. Invalidate permission cache
      await queryClient.invalidateQueries(['permissions', 'access-context']);
      
      // 3. Refetch access-context with new account
      const newContext = await queryClient.fetchQuery({
        queryKey: ['permissions', 'access-context', accountId],
        queryFn: async () => {
          const params = accountId ? { account_id: accountId } : {};
          const response = await api.get('/auth/me/access-context', { params });
          return response.data;
        },
      });
      
      // MA-PHASE11A: Debug logging removed for production
      // console.log('[AccountSwitch] Context loaded:', { accountId, modules: newContext.effective_modules });

      // 4. Check if current route is still accessible
      const currentPath = location.pathname;
      const newEffectiveModules = newContext.effective_modules || [];
      
      // Determine if current route is allowed
      // P4-WP-3: derived from navigationConfig, the canonical navigation
      // declaration. The local routeModuleMap this replaced had drifted out
      // of sync (it was missing `emails`).
      const isRouteAllowed = isPathReachable(currentPath, newEffectiveModules, accountId);

      // 5. Navigate appropriately and show toast AFTER state settles
      if (isRouteAllowed) {
        // Stay on current route - toast after state update
        // console.log('[AccountSwitch] COMPLETE - staying on route:', currentPath);
        toast({
          title: "Account Switched",
          description: `Now viewing ${accounts.find(a => a.account_id === accountId)?.account_name}`,
        });
      } else {
        // Redirect to first accessible route
        const firstAccessibleRoute = getFirstReachablePath(newEffectiveModules, accountId);
        // console.log('[AccountSwitch] COMPLETE - redirecting to:', firstAccessibleRoute);
        navigate(firstAccessibleRoute, { replace: true });
        toast({
          title: "Account Switched",
          description: `Switched to ${accounts.find(a => a.account_id === accountId)?.account_name}`,
        });
      }
    } catch (error) {
      console.error('[AccountSwitch] ERROR:', error);
      toast({
        title: "Error",
        description: "Failed to switch account. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle sub-account creation success
  const handleSubAccountCreated = async (createdAccount) => {
    // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001: Use refreshAccounts from TenantContext
    try {
      // Refetch accounts list through TenantContext
      await refreshAccounts();
      
      // Auto-switch to the newly created account
      await handleSwitchAccount(createdAccount.account_id);
    } catch (error) {
      console.error('Failed to refresh accounts:', error);
      // Still show success, just manual refresh needed
      toast({
        title: "Success",
        description: "Sub-account created. Refresh to see it in the list.",
      });
    }
  };

  // Don't render if not visible
  if (!accountsLoading && !shouldShow) {
    return null;
  }

  if (accountsLoading) {
    return (
      <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2 px-3'} py-1.5 text-sm text-slate-200/70`}>
        <Building2 className="h-4 w-4" />
        {!collapsed && <span>Loading...</span>}
      </div>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {collapsed ? (
            // Collapsed: Match navigation icon pattern (centered, no permanent highlight)
            <button
              className="group grid w-full items-center rounded-xl text-sm text-slate-100 transition grid-cols-[96px] focus:outline-none focus-visible:outline-none"
              data-testid="account-switcher-trigger"
            >
              <div className="flex h-10 w-[96px] items-center justify-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full transition group-hover:bg-indigo-500/10">
                  <Building2 className="h-[22px] w-[22px] text-slate-100" />
                </div>
              </div>
            </button>
          ) : (
            // Expanded: Full display with icon, name, role, and chevron
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 px-3 py-1.5 text-left hover:bg-white/5"
              data-testid="account-switcher-trigger-expanded"
            >
              <Building2 className="h-4 w-4 text-slate-200/70 flex-shrink-0" />
              <div className="flex flex-col overflow-hidden flex-1 min-w-0">
                <span className="text-sm font-medium text-slate-100 truncate">
                  {currentAccount?.account_name || 'Select Account'}
                </span>
                <span className="text-xs text-slate-200/70">
                  {currentAccount?.role 
                    ? currentAccount.role.charAt(0).toUpperCase() + currentAccount.role.slice(1)
                    : ''}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-200/70 flex-shrink-0" />
            </Button>
          )}
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="start" 
          className="w-80 bg-slate-950 text-slate-100 ring-1 ring-white/10"
          data-testid="account-switcher-content"
        >
          {/* CORRECTION: Search input ALWAYS at top */}
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-200/50" />
              <Input
                placeholder="Search accounts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 bg-slate-900 border-white/10 text-slate-100"
                data-testid="account-search-input"
              />
            </div>
          </div>
          
          <DropdownMenuSeparator className="bg-white/10" />
          
          <DropdownMenuLabel className="text-xs text-slate-200/70 uppercase">
            Your Accounts
          </DropdownMenuLabel>
          
          <div className="max-h-64 overflow-y-auto">
            {filteredAccounts.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-200/50 text-center">
                No accounts found
              </div>
            ) : (
              filteredAccounts.map((account) => (
                <DropdownMenuItem
                  key={account.account_id}
                  onClick={() => handleSwitchAccount(account.account_id)}
                  className="flex items-center gap-3 cursor-pointer"
                  data-testid={`account-option-${account.account_id}`}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-indigo-500/20 text-indigo-300">
                    {account.account_name?.charAt(0).toUpperCase() || 'A'}
                  </div>
                  
                  <div className="flex flex-1 flex-col overflow-hidden">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {account.account_name}
                      </span>
                      {account.account_id === selectedTenantId && (
                        <Check className="h-4 w-4 text-emerald-400" />
                      )}
                    </div>
                    <span className="text-xs text-slate-200/70">
                      {account.role} • {account.plan || 'Pro'}
                      {account.parent_account_id && ' • Sub-account'}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))
            )}
          </div>
          
          {/* Create Sub-Account Button (only if can_manage) */}
          {currentAccount?.can_manage && (
            <>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={() => setCreateModalOpen(true)}
                className="flex items-center gap-2 cursor-pointer text-indigo-300"
                data-testid="create-subaccount-button"
              >
                <Plus className="h-4 w-4" />
                Create Sub-Account
              </DropdownMenuItem>
            </>
          )}
          
          {/* Manage Accounts Link (only if can_manage) */}
          {currentAccount?.can_manage && (
            <DropdownMenuItem
              onClick={() => navigate('/app/accounts')}
              className="flex items-center gap-2 cursor-pointer"
              data-testid="manage-accounts-button"
            >
              <SettingsIcon className="h-4 w-4" />
              Manage Accounts
            </DropdownMenuItem>
          )}
          
          {isSuperAdmin && (
            <>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuLabel className="text-xs text-amber-200/70">
                SuperAdmin Mode Active
              </DropdownMenuLabel>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create Sub-Account Modal */}
      <CreateSubAccountModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={handleSubAccountCreated}
      />
    </>
  );
}

export default AccountSwitcher;
